import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { createHoliday, listHolidays } from "../../api/holidays";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import { fromDateTimeLocalValue } from "../../utils/dateTimeLocal";

const HOLIDAYS_CACHE_KEY = "cehr_company_holidays_v1";

const Holidays = () => {
  const cachedData = useMemo(
    () => readSessionCache(HOLIDAYS_CACHE_KEY, { holidays: [] }),
    [],
  );
  const [holidays, setHolidays] = useState(cachedData?.holidays || []);
  const [isLoading, setIsLoading] = useState(!cachedData?.holidays?.length);
  const [errorMessage, setErrorMessage] = useState("");
  const [openHolidayModal, setOpenHolidayModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [holidayName, setHolidayName] = useState("");
  const [holidayDescription, setHolidayDescription] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [category, setCategory] = useState("Company");
  const [status, setStatus] = useState("Active");
  const [notifyStaff, setNotifyStaff] = useState(false);
  const [notifyAt, setNotifyAt] = useState("");

  const navigate = useNavigate();

  const loadHolidays = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent && holidays.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await listHolidays();
        const nextHolidays = response?.holidays || [];
        setHolidays(nextHolidays);
        writeSessionCache(HOLIDAYS_CACHE_KEY, { holidays: nextHolidays });
      } catch (error) {
        const responseMessage =
          error?.message || "Unable to load holidays. Please try again.";
        setErrorMessage(responseMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [holidays.length],
  );

  useEffect(() => {
    loadHolidays({ silent: true });
  }, [loadHolidays]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadHolidays({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadHolidays]);

  const resetForm = () => {
    setHolidayName("");
    setHolidayDescription("");
    setStartAt("");
    setEndAt("");
    setCategory("Company");
    setStatus("Active");
    setNotifyStaff(false);
    setNotifyAt("");
    setErrorMessage("");
  };

  const closeHolidayModal = () => {
    setOpenHolidayModal(false);
    resetForm();
  };

  const handleCreateHoliday = async (ev) => {
    ev.preventDefault();

    if (!holidayName.trim()) {
      setErrorMessage("Holiday name is required.");
      return;
    }

    if (!startAt) {
      setErrorMessage("Holiday date is required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await createHoliday({
        holidayName: holidayName.trim(),
        holidayDescription: holidayDescription.trim() || undefined,
        category,
        status,
        startAt: fromDateTimeLocalValue(startAt),
        endAt: fromDateTimeLocalValue(endAt),
        notifyStaff,
        notifyAt: notifyStaff ? fromDateTimeLocalValue(notifyAt) : undefined,
      });

      closeHolidayModal();
      setOpenSuccessModal(true);
      await loadHolidays();
    } catch (error) {
      const responseMessage =
        error?.message || "Unable to create holiday. Please try again.";
      setErrorMessage(responseMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const rows = holidays.map((holiday) => ({
    ...holiday,
    holidayDate: formatDate(holiday.startAt),
  }));

  const headings = ["Holiday Name", "Date", "Category", "Status", "Actions"];
  const columns = ["holidayName", "holidayDate", "category", "status"];

  return (
    <div className="p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Holidays" },
        ]}
        title="Holidays"
        subtitle="Create, schedule, and notify staff about upcoming holidays."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={() => setOpenHolidayModal(true)}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      {isLoading && rows.length === 0 ? (
        <FetchLoadingState
          message="Loading holidays..."
          className="min-h-[60vh]"
        />
      ) : null}

      {errorMessage && rows.length === 0 ? (
        <div
          className="px-6 py-4 text-sm text-red-600"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={rows}
        showEmptyState={!isLoading}
        emptyMessage="No holidays found. Create one to get started."
        onView={(row) => {
          sessionStorage.setItem("selectedHolidayId", row.id);
          navigate("/company-settings/holiday-details", {
            state: { holidayId: row.id },
          });
        }}
      />

      <InviteModal
        isOpen={openHolidayModal}
        onClose={closeHolidayModal}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Nunito">
              Create Holiday
            </p>
          </div>
          <form
            className="my-8"
            onSubmit={handleCreateHoliday}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="holiday-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Holiday Name
              </label>
              <input
                type="text"
                id="holiday-name"
                placeholder="Holiday name"
                value={holidayName}
                onChange={(ev) => setHolidayName(ev.target.value)}
                className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="holiday-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Description
              </label>
              <textarea
                id="holiday-description"
                placeholder="Optional holiday description"
                value={holidayDescription}
                onChange={(ev) => setHolidayDescription(ev.target.value)}
                className="w-full p-2 min-h-24 mb-3 text-center rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="holiday-start"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  id="holiday-start"
                  value={startAt}
                  onChange={(ev) => setStartAt(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>

              <div>
                <label
                  htmlFor="holiday-end"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  End Date & Time (optional)
                </label>
                <input
                  type="datetime-local"
                  id="holiday-end"
                  value={endAt}
                  onChange={(ev) => setEndAt(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="holiday-category"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Category
                </label>
                <select
                  id="holiday-category"
                  value={category}
                  onChange={(ev) => setCategory(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                >
                  <option value="Company">Company</option>
                  <option value="Public">Public</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="holiday-status"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Status
                </label>
                <select
                  id="holiday-status"
                  value={status}
                  onChange={(ev) => setStatus(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="flex items-center gap-2 text-sm font-medium text-black">
                <input
                  type="checkbox"
                  checked={notifyStaff}
                  onChange={(ev) => setNotifyStaff(ev.target.checked)}
                />
                Notify staff about this holiday
              </label>
            </div>

            {notifyStaff ? (
              <div className="mb-3">
                <label
                  htmlFor="notify-at"
                  className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
                >
                  Schedule notification (optional)
                </label>
                <input
                  type="datetime-local"
                  id="notify-at"
                  value={notifyAt}
                  onChange={(ev) => setNotifyAt(ev.target.value)}
                  className="w-full p-2 text-center mb-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
                />
              </div>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#1234CD] text-white py-3 rounded-xl mt-4 disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create Holiday"}
            </button>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      >
        <div className="text-center">
          <h2 className="text-black text-center text-3xl leading-[27px] font-bold mb-2">
            Holiday created
          </h2>
          <p className="text-black text-center text-lg leading-[18.9px]">
            Your holiday has been saved successfully.
          </p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default Holidays;
